import React from 'react';
import { ILicenseUsage } from '../../../../types/usageTypes/ILicenseUsage';
import { IUsageSearchResultRowType } from '../../../../types/usageTypes/IUsageGroupsSearchResult';
import { UsageService } from '../../../../services/usageServices/UsageServices';
import { MessageBanner, MessageType } from '../../../../components/messageBanner/MessageBanner';
import { ISplitLicenseResult } from '../../../../types/usageTypes/ISplitLicenseResult';

interface ISplitLicenseViewProps {
    sourceUsageGroupId: string;
    sourceLicense: ILicenseUsage;
    targetUsageGroup: IUsageSearchResultRowType;
    onComplete: (result: ISplitLicenseResult) => void;
}

const SplitLicenseView: React.FC<ISplitLicenseViewProps> = ({
    sourceUsageGroupId,
    sourceLicense,
    targetUsageGroup,
    onComplete,
}) => {
    const originalTotalFee = sourceLicense.licenseFee ?? 0;
    const [originalLicenseFee, setOriginalLicenseFee] = React.useState<number | ''>(originalTotalFee);
    const [newLicenseFee, setNewLicenseFee] = React.useState<number | ''>('');
    const [newLicensingSystemId, setNewLicensingSystemId] = React.useState('');
    const [validationMessages, setValidationMessages] = React.useState<string[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);

    const originalLicenseFeeValue = Number(originalLicenseFee);
    const newLicenseFeeValue = Number(newLicenseFee);
    const resultingTotal = originalLicenseFeeValue + newLicenseFeeValue;
    const hasInvalidLicenseFee = originalLicenseFeeValue <= 0 || newLicenseFeeValue <= 0;
    const isInvalid = hasInvalidLicenseFee || resultingTotal > originalTotalFee;

    const completeSplit = async () => {
        if (!newLicensingSystemId.trim()) {
            setValidationMessages(['Licensing System ID is required.']);
            return;
        }
        if (hasInvalidLicenseFee) {
            setValidationMessages(['Both license fees must be greater than zero.']);
            return;
        }
        if (resultingTotal > originalTotalFee) {
            setValidationMessages(['The resulting license fees cannot exceed the original License Fee.']);
            return;
        }

        setIsSaving(true);
        setValidationMessages([]);
        try {
            const result = await UsageService.splitLicense({
                sourceUsageGroupId,
                targetUsageGroupId: targetUsageGroup.key,
                sourceLicensingSystemId: sourceLicense.licensingSystemID,
                newLicensingSystemId: newLicensingSystemId.trim(),
                originalLicenseFee: originalLicenseFeeValue,
                newLicenseFee: newLicenseFeeValue,
            });
            if (!result.isValid || result.validationMessage?.length) {
                const resultMessages = (result.validationMessage ?? []).map(message =>
                    `${message.messageKey}${message.additionalInfo ? ` ${message.additionalInfo}` : ''}`
                );
                setValidationMessages(resultMessages.length > 0
                    ? resultMessages
                    : [result.validationError ?? 'The license split could not be completed.']);
                return;
            }

            onComplete(result);
        } catch (requestError: any) {
            setValidationMessages([
                requestError?.response?.data?.message ?? 'The license split could not be completed.'
            ]);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="splitLicenseView">
            {validationMessages.length > 0 &&
                <MessageBanner
                    messages={validationMessages}
                    messageType={MessageType.Danger}
                    resetMessageBanner={() => setValidationMessages([])}
                />}
            <div className="form-group">
                <label>Target Usage Group</label>
                <div>{targetUsageGroup.title || targetUsageGroup.key}</div>
            </div>
            <div className="form-group">
                <label htmlFor="newLicensingSystemId">Licensing System ID</label>
                <input id="newLicensingSystemId" className="form-control" value={newLicensingSystemId}
                    onChange={event => setNewLicensingSystemId(event.target.value)} />
            </div>
            <div className="form-group">
                <label htmlFor="originalLicenseFee">Original License Fee</label>
                <input id="originalLicenseFee" className="form-control" type="number" min="0" step="0.01"
                    value={originalLicenseFee} onChange={event => setOriginalLicenseFee(event.target.value === '' ? '' : Number(event.target.value))} />
            </div>
            <div className="form-group">
                <label htmlFor="newLicenseFee">New License Fee</label>
                <input id="newLicenseFee" className="form-control" type="number" min="0" step="0.01"
                    value={newLicenseFee} onChange={event => setNewLicenseFee(event.target.value === '' ? '' : Number(event.target.value))} />
            </div>
            <div className="form-group">Original total: {originalTotalFee}. Resulting total: {resultingTotal}.</div>
            <button type="button" className="button btn-defaultPrimary" disabled={isSaving || isInvalid || !newLicensingSystemId.trim()}
                onClick={completeSplit}>
                {isSaving ? 'Completing...' : 'Complete Split'}
            </button>
        </div>
    );
};

export default SplitLicenseView;
